import React, {useEffect, useRef, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {Chart, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Legend} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {createChart} from '../../slidesParts/NewAmplifyCalculator/helpers';
import {palette} from '../../helpers/theme';
import {
    barData,
    barDataMobile,
    curveData,
    curveDataMobile,
    options,
    optionsMobile,
    dot1Data,
    dot2Data,
    dot1DataMobile,
    dot2DataMobile,
} from './constants';

Chart.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Legend, annotationPlugin);

const styles = {
    title: {
        fontSize: 36,
        lineHeight: {xs: '120%', md: '150%'},
        fontFamily: {xs: 'FredokaSemiBold', md: 'FredokaRegular'},
        textAlign: {xs: 'center', md: 'left'},
        mb: {xs: 2, md: 4},
        mx: {xs: 'auto', md: 'initial'},
        color: palette.secondary[500],
    },
    legendItem: {
        fontSize: {md: 24},
    },
    line: {
        width: '24px',
        borderRadius: '4px',
        border: '4px solid #5AE288',
    },
    content: {
        mx: {xs: 4, md: 'auto'},
        maxWidth: '794px',
    },
};

const ChartComponent: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [chart, setChart] = useState<Chart<'bar' | 'line'> | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        if (canvasRef.current) {
            if (chart) {
                chart.destroy();
            }

            const chartConfig = {
                type: 'bar',
                data: {
                    labels: [...Array.from({length: 18}, (_, i) => i + 1)],
                    datasets: [barData, curveData, dot1Data, dot2Data],
                },
                options,
            };

            const chartConfigMobile = {
                type: 'bar',
                data: {
                    labels: [...Array.from({length: 5}, (_, i) => i + 1)],
                    datasets: [barDataMobile, curveDataMobile, dot1DataMobile, dot2DataMobile],
                },
                options: optionsMobile,
            };

            if (chart === null) {
                createChart('ChartDesktop', chartConfig).then(setChart);
                createChart('ChartMobile', chartConfigMobile).then(setChart);

                return;
            }
        }

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, []);

    return (
        <Box mt={{xs: 6, md: 11}}>
            <Box sx={styles.content}>
                <Typography component="h2" sx={styles.title}>
                    IUL Benefits Over Time
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={styles.line} />
                    <Typography variant="subtitle1" sx={styles.legendItem}>
                        IUL Policy Value
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{...styles.line, borderColor: '#F4F0D7'}} />
                    <Typography variant="subtitle1" sx={styles.legendItem}>
                        Index Performance
                    </Typography>
                </Stack>
            </Box>
            <Box
                sx={{
                    display: {md: 'none'},
                    height: '360px',
                }}
            >
                <canvas id="ChartMobile" ref={canvasRef} style={{width: '100%', height: '100%'}} />
            </Box>
            <Box
                sx={{
                    display: {xs: 'none', md: 'block'},
                    height: '400px',
                    width: {md: '80%'},
                    mx: 'auto',
                }}
            >
                <canvas id="ChartDesktop" ref={canvasRef} style={{width: '100%', height: '100%'}} />
            </Box>
        </Box>
    );
};

export default ChartComponent;
